import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { Brain, Users, Contact, Database, ArrowRight, Plus, Mail, Phone, MessageCircle, CheckCircle, XCircle, Clock, MessageSquare, Mic, Volume2, Bell, AlertTriangle, Ban, HandshakeIcon, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { VoiceUpsellDialog } from '../components/voice/VoiceUpsellDialog';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SourceBadge({ source }) {
  if (!source?.connected) {
    return <Badge variant="outline" className="text-xs gap-1 text-muted-foreground"><XCircle className="w-3 h-3" /> Off</Badge>;
  }
  const status = source.status || 'connected';
  if (status === 'completed' || status === 'connected') {
    return <Badge variant="default" className="text-xs gap-1 bg-emerald-600 hover:bg-emerald-700"><CheckCircle className="w-3 h-3" /> OK</Badge>;
  }
  if (status === 'failed') {
    return <Badge variant="destructive" className="text-xs gap-1"><XCircle className="w-3 h-3" /> Err</Badge>;
  }
  return <Badge variant="secondary" className="text-xs gap-1"><Clock className="w-3 h-3" /> Sync</Badge>;
}

function OnboardingStep({ number, title, description, done, action, actionLabel }) {
  return (
    <div className={`flex items-center gap-4 p-4 rounded-lg border transition-colors ${done ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-muted/50 border-border'}`}>
      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${done ? 'bg-emerald-500 text-white' : 'bg-primary/10 text-primary'}`}>
        {done ? <CheckCircle className="w-4 h-4" /> : number}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`font-medium text-sm ${done ? 'text-emerald-700 dark:text-emerald-400' : ''}`}>{title}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      {!done && action && (
        <Button variant="outline" size="sm" onClick={action} data-testid={`onboarding-step-${number}`}>
          {actionLabel || 'Start'}
        </Button>
      )}
    </div>
  );
}

export default function DashboardPage() {
  const { token, user, tenant } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    fetchStats();
    fetchAnalytics();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/tenants/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const res = await axios.get(`${API}/tenants/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(res.data);
    } catch (e) {
      // Analytics may not be available for all roles
    }
  };

  const isOwner = user?.role === 'owner';
  const features = tenant?.features || {};
  const voiceNotActive = !features.voice_standard && !features.voice_clone;
  const [upsellOpen, setUpsellOpen] = useState(false);

  // Notifications for owner
  const [notifications, setNotifications] = useState({ unread_count: 0, notifications: [] });
  useEffect(() => {
    if (isOwner && token) {
      axios.get(`${API}/tenant/notifications`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setNotifications(res.data))
        .catch(() => {});
    }
  }, [isOwner, token]);

  const clonesList = stats?.clones?.list || [];
  const hasClones = clonesList.length > 0;
  const hasAnySource = clonesList.some(c => c.sources?.email?.connected || c.sources?.ringover?.connected || c.sources?.whatsapp?.connected);
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';

  const statCards = [
    { title: 'Clones', value: stats?.clones?.active || 0, total: stats?.clones?.limit || 5, icon: Brain, color: 'text-indigo-500', bgColor: 'bg-indigo-500/10', path: '/clones' },
    { title: 'Memories', value: stats?.memories?.count || 0, total: null, icon: Database, color: 'text-rose-500', bgColor: 'bg-rose-500/10', path: '/clones' },
    ...(isAdmin ? [
      { title: 'Contacts', value: stats?.contacts?.count || 0, total: null, icon: Contact, color: 'text-amber-500', bgColor: 'bg-amber-500/10', path: '/contacts' },
      { title: 'Team', value: stats?.users?.count || 0, total: stats?.users?.limit || 10, icon: Users, color: 'text-emerald-500', bgColor: 'bg-emerald-500/10', path: '/users' },
    ] : []),
  ];

  const memoryTypes = stats?.memories?.by_type || {};

  return (
    <DashboardLayout title="Dashboard">
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Welcome */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-heading font-bold tracking-tight" data-testid="welcome-title">
              Bonjour, {user?.first_name}
            </h2>
            <p className="text-muted-foreground mt-1 text-sm">{tenant?.name}</p>
          </div>
          {isAdmin && (
            <Button onClick={() => navigate('/clones')} className="gap-2" data-testid="create-clone-btn">
              <Plus className="w-4 h-4" /> Nouveau clone
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stats-grid">
          {statCards.map((stat, i) => (
            <Card key={i} className="group cursor-pointer hover:border-primary/50 transition-colors" onClick={() => navigate(stat.path)} data-testid={`stat-card-${i}`}>
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{stat.title}</span>
                  <div className={`p-1.5 rounded-md ${stat.bgColor}`}><stat.icon className={`w-3.5 h-3.5 ${stat.color}`} /></div>
                </div>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-2xl font-heading font-bold">{stat.value}</span>
                  {stat.total !== null && <span className="text-xs text-muted-foreground">/ {stat.total}</span>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Memory breakdown */}
        {Object.keys(memoryTypes).length > 0 && (
          <div className="flex flex-wrap gap-3" data-testid="memory-breakdown">
            {memoryTypes.email > 0 && <Badge variant="outline" className="gap-1.5 py-1"><Mail className="w-3 h-3" /> {memoryTypes.email} emails</Badge>}
            {memoryTypes.call > 0 && <Badge variant="outline" className="gap-1.5 py-1"><Phone className="w-3 h-3" /> {memoryTypes.call} appels</Badge>}
            {memoryTypes.sms > 0 && <Badge variant="outline" className="gap-1.5 py-1"><MessageSquare className="w-3 h-3" /> {memoryTypes.sms} SMS</Badge>}
            {memoryTypes.whatsapp > 0 && <Badge variant="outline" className="gap-1.5 py-1"><MessageCircle className="w-3 h-3" /> {memoryTypes.whatsapp} WhatsApp</Badge>}
            {memoryTypes.note > 0 && <Badge variant="outline" className="gap-1.5 py-1"><Database className="w-3 h-3" /> {memoryTypes.note} notes</Badge>}
          </div>
        )}

        <div className={`grid grid-cols-1 ${isAdmin ? 'lg:grid-cols-2' : ''} gap-6`}>
          {/* Onboarding — admin only */}
          {isAdmin && (
          <Card data-testid="onboarding-card">
            <CardHeader className="pb-4">
              <CardTitle className="font-heading text-base">Mise en route</CardTitle>
              <CardDescription className="text-xs">Configurez Clone IA en 5 etapes</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <OnboardingStep number={1} title="Creer un clone" description="Ajoutez un collaborateur comme clone numerique" done={hasClones} action={() => navigate('/clones')} actionLabel="Creer" />
              <OnboardingStep number={2} title="Connecter un email" description="Configurez IMAP pour ingerer les emails" done={clonesList.some(c => c.sources?.email?.connected)} action={hasClones ? () => navigate(`/clones/${clonesList[0]?.id}/email`) : null} actionLabel="Connecter" />
              <OnboardingStep number={3} title="Connecter Ringover" description="Appels et SMS via l'API Ringover" done={clonesList.some(c => c.sources?.ringover?.connected)} action={hasClones ? () => navigate(`/clones/${clonesList[0]?.id}/ringover`) : null} actionLabel="Connecter" />
              <OnboardingStep number={4} title="Connecter WhatsApp" description="Configurez le numero WhatsApp du clone" done={clonesList.some(c => c.sources?.whatsapp?.connected)} action={hasClones ? () => navigate(`/clones/${clonesList[0]?.id}/sources`) : null} actionLabel="Configurer" />
              <OnboardingStep number={5} title="Tester le chat" description="Interrogez votre clone en langage naturel" done={false} action={hasClones ? () => navigate(`/clones/${clonesList[0]?.id}/chat`) : null} actionLabel="Chat" />
            </CardContent>
          </Card>
          )}

          {/* Clones Overview */}
          <Card data-testid="clones-overview-card">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading text-base">Clones</CardTitle>
                  <CardDescription className="text-xs">Etat des sources et memories</CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={() => navigate('/clones')} className="gap-1 text-xs" data-testid="view-all-clones">
                  Voir tout <ArrowRight className="w-3 h-3" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {!hasClones ? (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  <Brain className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p>Aucun clone. Creez votre premier clone pour commencer.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {clonesList.slice(0, 5).map(clone => (
                    <div key={clone.id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border/50 hover:border-border transition-colors" data-testid={`clone-overview-${clone.id}`}>
                      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
                        {clone.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{clone.name}</p>
                        <div className="flex items-center gap-1.5 mt-1">
                          <span className="text-xs text-muted-foreground">{clone.memory_count || 0} mem.</span>
                          <span className="text-muted-foreground/30">|</span>
                          <div className="flex items-center gap-1" title="Email"><Mail className="w-3 h-3 text-muted-foreground" /><SourceBadge source={clone.sources?.email} /></div>
                          <div className="flex items-center gap-1" title="Ringover"><Phone className="w-3 h-3 text-muted-foreground" /><SourceBadge source={clone.sources?.ringover} /></div>
                          <div className="flex items-center gap-1" title="WhatsApp"><MessageCircle className="w-3 h-3 text-muted-foreground" /><SourceBadge source={clone.sources?.whatsapp} /></div>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" className="gap-1 text-xs flex-shrink-0" onClick={() => navigate(`/clones/${clone.id}/chat`)} data-testid={`quick-chat-${clone.id}`}>
                        <MessageSquare className="w-3.5 h-3.5" /> Chat
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Voice IA Teasing Card (owner only, not yet active) */}
        {isOwner && voiceNotActive && (
          <Card className="border-blue-500/20 bg-gradient-to-r from-blue-500/5 to-violet-500/5" data-testid="voice-teaser-card">
            <CardContent className="py-5 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500/15 to-violet-500/15 shrink-0">
                <Mic className="w-6 h-6 text-blue-500" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-sm mb-0.5">Activer la Voix IA</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Vos collaborateurs peuvent consulter les dossiers et interactions en vocal.
                </p>
                <div className="flex items-center gap-4 mt-1.5">
                  <span className="text-xs text-blue-600 font-medium">Standard : +19 EUR/utilisateur</span>
                  <span className="text-xs text-violet-600 font-medium">Premium : +39 EUR/utilisateur</span>
                </div>
              </div>
              <Button size="sm" onClick={() => setUpsellOpen(true)} className="shrink-0 gap-1.5" data-testid="discover-voice-btn">
                <Volume2 className="w-4 h-4" /> Decouvrir la voix IA
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Owner Notifications */}
        {isOwner && notifications.unread_count > 0 && (
          <Card className="border-amber-500/20" data-testid="owner-notifications-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Bell className="w-4 h-4 text-amber-500" />
                Notifications
                <Badge className="bg-amber-500 text-white text-[10px] px-1.5">{notifications.unread_count}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {notifications.notifications.filter(n => !n.read).slice(0, 3).map(n => (
                <div key={n.id} className="flex items-start gap-3 p-2.5 rounded-md bg-amber-50 dark:bg-amber-500/5 border border-amber-200/50 text-sm">
                  <Mic className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs font-medium">{n.title}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{n.message}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0 text-xs h-7"
                    onClick={() => setUpsellOpen(true)}
                  >
                    Activer
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Tenant Analytics */}
      {analytics && (isOwner || user?.role === 'admin') && (
        <Card data-testid="tenant-analytics-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-blue-500" />
              Suivi Entreprise
            </CardTitle>
            <CardDescription className="text-xs">Promesses et blocages de vos collaborateurs</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Alert summary */}
            <div className="flex gap-3 mb-4" data-testid="analytics-summary">
              {analytics.total_critical > 0 && (
                <Badge variant="destructive" className="text-xs gap-1">
                  <AlertTriangle className="w-3 h-3" /> {analytics.total_critical} critique{analytics.total_critical > 1 ? 's' : ''}
                </Badge>
              )}
              {analytics.total_warning > 0 && (
                <Badge variant="outline" className="text-xs gap-1 bg-amber-500/10 text-amber-700 border-amber-200">
                  <AlertTriangle className="w-3 h-3" /> {analytics.total_warning} warning
                </Badge>
              )}
              {analytics.total_alertes === 0 && (
                <Badge variant="outline" className="text-xs gap-1 bg-emerald-500/10 text-emerald-700 border-emerald-200">
                  <CheckCircle className="w-3 h-3" /> Aucune alerte
                </Badge>
              )}
            </div>

            {/* Clones table */}
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Collaborateur</th>
                    <th className="text-center px-3 py-2 text-xs font-medium text-muted-foreground">
                      <span className="flex items-center justify-center gap-1"><Ban className="w-3 h-3" /> Blocages</span>
                    </th>
                    <th className="text-center px-3 py-2 text-xs font-medium text-muted-foreground">
                      <span className="flex items-center justify-center gap-1"><HandshakeIcon className="w-3 h-3" /> Promesses retard</span>
                    </th>
                    <th className="text-center px-3 py-2 text-xs font-medium text-muted-foreground">Alertes</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.clones.map(c => (
                    <tr key={c.person_id} className="border-t" data-testid={`analytics-row-${c.person_id}`}>
                      <td className="px-3 py-2 font-medium">{c.name}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={c.blocages_active > 0 ? 'text-red-600 font-semibold' : 'text-muted-foreground'}>{c.blocages_active}</span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className={c.promesses_overdue > 0 ? 'text-amber-600 font-semibold' : 'text-muted-foreground'}>{c.promesses_overdue}</span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        {c.alertes_critical > 0 ? (
                          <Badge variant="destructive" className="text-[10px]">{c.alertes_critical}</Badge>
                        ) : c.alertes_count > 0 ? (
                          <Badge variant="outline" className="text-[10px] bg-amber-500/10 text-amber-700">{c.alertes_count}</Badge>
                        ) : (
                          <span className="text-muted-foreground text-xs">0</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <VoiceUpsellDialog
        open={upsellOpen}
        onOpenChange={setUpsellOpen}
        requiredFeature="voice_standard"
      />
    </DashboardLayout>
  );
}
